import type { ImpactType } from './articles/data'
import { IMPACT_INTROS } from './impact-intros'
import { METALS } from './metals/data'
import type { I18nDescriptor } from './metals/types'
import translations from './videos.translations'

/** Self-hosted video (Cloudflare R2); `webm` preferred, `mp4` the fallback. */
export interface VideoSources {
  webm?: string
  mp4: string
}

export interface GalleryVideo {
  /** Cinema id — the `?video=` param value. */
  id: string
  /** Asset base name on the bucket: `<name>_<locale>.{webm,mp4}`. */
  name: string
  title: I18nDescriptor
  /** Still shown while the entry sits idle. Authored per subject, not derived. */
  poster: string
}

const VIDEO_BASE = 'https://raw-materials-videos.statics.backmarket.com'

/** `<name>_<locale>.mp4` — locale verbatim (`cobalt_fr-fr`); an absent asset
 *  404s, with no fallback. */
export function videoSources(name: string, locale: string): VideoSources {
  return {
    // No webm on the bucket yet; both players already read this field.
    // webm: `${VIDEO_BASE}/${name}_${locale}.webm`,
    mp4: `${VIDEO_BASE}/${name}_${locale}.mp4`,
  }
}

/** The campaign film shown in the intro modal, and the gallery's first entry. */
export const INTRO_VIDEO: GalleryVideo = {
  id: 'intro',
  name: 'intro',
  title: translations.introTitle,
  poster: '/mine-my-phone/images/posters/intro.png',
}

/** The intro's silent clip: no gallery entry, no cinema id, no poster. */
export const ONBOARDING_VIDEO = 'onboarding'

export function impactVideoId(type: ImpactType): string {
  return `impact-${type}`
}

export function metalVideoId(slug: string): string {
  return `metal-${slug}`
}

// Assembled from where each subject lives; order is intro -> impacts -> metals.
export const VIDEOS: GalleryVideo[] = [
  INTRO_VIDEO,
  // The record key is the asset name, so impacts carry no name field of their own.
  ...Object.entries(IMPACT_INTROS).map(([type, intro]) => ({
    id: impactVideoId(type as ImpactType),
    name: type,
    title: intro.title,
    poster: intro.poster,
  })),
  ...METALS.flatMap((metal) =>
    metal.video
      ? [
          {
            id: metalVideoId(metal.slug),
            name: metal.video.name,
            title: metal.name,
            poster: metal.video.poster,
          },
        ]
      : [],
  ),
]
